/**
 * organizationStore.ts
 * Store Zustand pour la gestion d'une organisation (portail propriétaire/manager).
 */
import { create } from 'zustand';
import { getAuthToken } from './index';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000/api';

const apiFetch = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const token = getAuthToken();
  const hasBody = options?.body !== undefined;
  const res = await fetch(`${API_URL}${path}`, {
    credentials: 'include',
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: `HTTP ${res.status}` })) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `HTTP ${res.status}`);
  }
  return res.json() as Promise<T>;
};

// ─── Types ────────────────────────────────────────────────────────────────────

export interface OrgMember {
  id: string;
  userId: string;
  organizationId: string;
  role: 'OWNER' | 'MANAGER' | 'MEMBER';
  status: 'PENDING' | 'ACTIVE' | 'REJECTED' | 'REVOKED';
  approvedAt: string | null;
  rejectedReason: string | null;
  createdAt: string;
  user: {
    id: string;
    email: string;
    name: string | null;
    avatarUrl: string | null;
  };
}

export interface OrgInvite {
  id: string;
  email: string;
  role: 'OWNER' | 'MANAGER' | 'MEMBER';
  status: 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REVOKED';
  expiresAt: string | null;
  createdAt: string;
}

export interface Organization {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  industry: string | null;
  website: string | null;
  address: string | null;
  totalSeats: number;
  usedSeats: number;
  plan: string;
  paymentStatus: 'ACTIVE' | 'TRIAL' | 'PAST_DUE' | 'SUSPENDED' | 'CANCELLED';
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  createdAt: string;
  owner: { id: string; email: string; name: string | null };
  orgMemberships: OrgMember[];
  invites: OrgInvite[];
}

// ─── State ────────────────────────────────────────────────────────────────────

interface OrgState {
  org: Organization | null;
  loading: boolean;
  error: string | null;
  actionLoading: string | null; // userId being processed

  fetchOrg: (orgId: string) => Promise<void>;
  approveMember: (orgId: string, userId: string) => Promise<void>;
  rejectMember: (orgId: string, userId: string, reason?: string) => Promise<void>;
  removeMember: (orgId: string, userId: string) => Promise<void>;
  inviteByEmail: (orgId: string, email: string, role?: string) => Promise<void>;
  updateOrg: (orgId: string, data: Partial<Pick<Organization, 'name' | 'industry' | 'website' | 'address' | 'logoUrl' | 'totalSeats'>>) => Promise<void>;
  clearError: () => void;
}

export const useOrganizationStore = create<OrgState>()((set, get) => ({
  org:           null,
  loading:       false,
  error:         null,
  actionLoading: null,

  fetchOrg: async (orgId) => {
    set({ loading: true, error: null });
    try {
      const { organization } = await apiFetch<{ organization: Organization }>(`/organizations/${orgId}`);
      set({ org: organization, loading: false });
    } catch (err) {
      set({ loading: false, error: (err as Error).message });
    }
  },

  approveMember: async (orgId, userId) => {
    set({ actionLoading: userId });
    try {
      await apiFetch(`/organizations/${orgId}/members/${userId}/approve`, { method: 'POST' });
      // Optimistic update: PENDING → ACTIVE
      set((s) => ({
        actionLoading: null,
        org: s.org ? {
          ...s.org,
          orgMemberships: s.org.orgMemberships.map((m) =>
            m.userId === userId ? { ...m, status: 'ACTIVE', approvedAt: new Date().toISOString() } : m
          ),
        } : null,
      }));
    } catch (err) {
      set({ actionLoading: null, error: (err as Error).message });
    }
  },

  rejectMember: async (orgId, userId, reason) => {
    set({ actionLoading: userId });
    try {
      await apiFetch(`/organizations/${orgId}/members/${userId}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      });
      set((s) => ({
        actionLoading: null,
        org: s.org ? {
          ...s.org,
          orgMemberships: s.org.orgMemberships.map((m) =>
            m.userId === userId ? { ...m, status: 'REJECTED', rejectedReason: reason ?? null } : m
          ),
        } : null,
      }));
    } catch (err) {
      set({ actionLoading: null, error: (err as Error).message });
    }
  },

  removeMember: async (orgId, userId) => {
    set({ actionLoading: userId });
    try {
      await apiFetch(`/organizations/${orgId}/members/${userId}`, { method: 'DELETE' });
      set((s) => ({
        actionLoading: null,
        org: s.org ? {
          ...s.org,
          orgMemberships: s.org.orgMemberships.filter((m) => m.userId !== userId),
        } : null,
      }));
    } catch (err) {
      set({ actionLoading: null, error: (err as Error).message });
    }
  },

  inviteByEmail: async (orgId, email, role = 'MEMBER') => {
    set({ actionLoading: 'invite' });
    try {
      await apiFetch(`/organizations/${orgId}/invites`, {
        method: 'POST',
        body: JSON.stringify({ email, role }),
      });
      // Reload org to get fresh invite list
      await get().fetchOrg(orgId);
      set({ actionLoading: null });
    } catch (err) {
      set({ actionLoading: null, error: (err as Error).message });
      throw err;
    }
  },

  updateOrg: async (orgId, data) => {
    try {
      const { organization } = await apiFetch<{ organization: Organization }>(
        `/organizations/${orgId}`,
        { method: 'PATCH', body: JSON.stringify(data) }
      );
      set({ org: organization });
    } catch (err) {
      set({ error: (err as Error).message });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));
